<template>
  <div>
    <form-section :title="$t('Integrations_Settings_BasicSettings')">
      <rest-form-item
        :label="$t('Integrations_Field_Name')"
        :hint="$t('Integrations_Field_Name_Description')"
        prop="name"
      >
        <el-input
          v-model="model.name"
          :placeholder="$t('Integrations_Field_Name')"
        />
      </rest-form-item>
    </form-section>

    <form-section :title="$t('Integrations_Settings_ConnectionSettings')">
      <el-row>
        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_Snowflake_Field_AccountName')"
            prop="params.account_name"
          >
            <el-input
              v-model="model.params.account_name"
              :placeholder="
                $t('Integrations_Snowflake_Field_AccountName_Placeholder')
              "
            />
          </rest-form-item>
        </el-col>

        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_Snowflake_Field_Username')"
            prop="params.username"
          >
            <el-input
              v-model="model.params.username"
              :placeholder="$t('Integrations_Snowflake_Field_Username')"
            />
          </rest-form-item>
        </el-col>
      </el-row>

      <el-row>
        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_Snowflake_Field_AuthMethod')"
            prop="params.auth_method"
          >
            <enum-select
              v-model="model.params.auth_method"
              :options="SnowflakeAuthenticationTypesLabels"
            />
          </rest-form-item>
        </el-col>

        <el-col :span="12" v-if="model.params.auth_method === 'key_pair'">
          <rest-form-item
            :label="$t('Integrations_Snowflake_Field_PrivateKey')"
            prop="params.private_key"
          >
            <div v-if="isEditPage && !changingPrivateKey">
              <el-button @click="changingPrivateKey = true">
                {{ $t('Integrations_Snowflake_Field_PrivateKey_Change') }}
              </el-button>
            </div>
            <template v-else>
              <el-input
                v-model="model.params.private_key"
                type="textarea"
                :placeholder="privateKeyPlaceholder"
                :rows="6"
              />
              <el-button
                v-if="isEditPage"
                type="text"
                size="small"
                @click="cancelPrivateKeyChange"
              >
                {{ $t('Button_Cancel') }}
              </el-button>
            </template>
          </rest-form-item>
        </el-col>

        <el-col :span="12" v-if="model.params.auth_method === 'password'">
          <rest-form-item
            :label="$t('Integrations_Snowflake_Field_Password')"
            prop="params.password"
          >
            <div v-if="isEditPage && !changingPassword">
              <el-button @click="changingPassword = true">
                {{ $t('Integrations_Snowflake_Field_Password_Change') }}
              </el-button>
            </div>
            <template v-else>
              <el-input
                v-model="model.params.password"
                type="password"
                show-password
                :placeholder="$t('Integrations_Snowflake_Field_Password')"
              />
              <el-button
                v-if="isEditPage"
                type="text"
                size="small"
                @click="cancelPasswordChange"
              >
                {{ $t('Button_Cancel') }}
              </el-button>
            </template>
          </rest-form-item>
        </el-col>
      </el-row>

      <el-row>
        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_Snowflake_Field_Warehouse')"
            prop="params.warehouse"
          >
            <el-input
              v-model="model.params.warehouse"
              :placeholder="$t('Integrations_Snowflake_Field_Warehouse')"
            />
          </rest-form-item>
        </el-col>

        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_Snowflake_Field_Database')"
            prop="params.database"
          >
            <el-input
              v-model="model.params.database"
              :placeholder="$t('Integrations_Snowflake_Field_Database')"
            />
          </rest-form-item>
        </el-col>
      </el-row>

      <el-row>
        <el-col :span="12">
          <rest-form-item
            :label="$t('Integrations_Snowflake_Field_Schema')"
            prop="params.schema"
          >
            <el-select v-model="schemaMode" @change="onSchemaModeChange">
              <el-option
                :label="$t('Integrations_Snowflake_Field_Schema_All')"
                value="all"
              />
              <el-option
                :label="$t('Integrations_Snowflake_Field_Schema_Specific')"
                value="specific"
              />
            </el-select>
          </rest-form-item>
        </el-col>

        <el-col v-if="schemaMode === 'specific'" :span="12">
          <rest-form-item
            :label="$t('Integrations_Snowflake_Field_SchemaName')"
            prop="params.schema"
          >
            <el-input
              v-model="model.params.schema"
              :placeholder="
                $t('Integrations_Snowflake_Field_SchemaName_Placeholder')
              "
            />
          </rest-form-item>
        </el-col>
      </el-row>
    </form-section>
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import RestFormItem from '@/common/components/rest/RestFormItem'
import EnumSelect from '@/common/components/form/EnumSelect'
import { SnowflakeAuthenticationTypesLabels } from '@/modules/integrations/enum/AuthenticationTypesEnum'

export default {
  name: 'SnowflakeSettings',

  components: {
    FormSection,
    RestFormItem,
    EnumSelect
  },

  props: {
    model: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      SnowflakeAuthenticationTypesLabels,
      privateKeyPlaceholder:
        '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----',
      changingPrivateKey: false,
      changingPassword: false,
      schemaMode: this.model.params.schema ? 'specific' : 'all'
    }
  },

  mounted() {
    if (!this.model.params.auth_method) {
      this.$set(this.model.params, 'auth_method', 'key_pair')
    }
  },

  methods: {
    cancelPrivateKeyChange() {
      this.model.params.private_key = ''
      this.changingPrivateKey = false
    },
    cancelPasswordChange() {
      this.model.params.password = ''
      this.changingPassword = false
    },
    onSchemaModeChange(mode) {
      if (mode === 'all') {
        this.model.params.schema = ''
      }
    }
  },

  computed: {
    isEditPage() {
      return !!this.$route.query.id
    },
    rules() {
      const isKeyPair = this.model.params.auth_method === 'key_pair'
      const isPassword = this.model.params.auth_method === 'password'

      return {
        name: {
          required: true,
          message: this.$t('RestForm_FieldIsRequired')
        },
        params: {
          auth_method: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          account_name: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          username: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          private_key: {
            required: isKeyPair && !this.isEditPage,
            message: this.$t('RestForm_FieldIsRequired')
          },
          password: {
            required: isPassword && !this.isEditPage,
            message: this.$t('RestForm_FieldIsRequired')
          },
          warehouse: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          database: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          }
        }
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/integrations/types/snowflake/SnowflakeSettings.vue